import { useCallback, useEffect, useRef, useState } from 'react';
import { consultationsApi } from './resources';
import { surRafraichissement } from './rafraichissement';
import { LANG, tr, trf } from '../lib/i18n';

/**
 * Les ouvertures d'offres et de factures par les clients.
 *
 * <p>« Quand le client ouvre l'offre ou la facture, je veux un signal à chaque
 * fois. » La liste se relit au rythme du bureau (chaque minute, au retour sur
 * l'application). Une ouverture arrivée depuis la dernière lecture devient
 * `nouvelle` : l'écran l'annonce le temps de la lire.
 *
 * @param {boolean} actif vrai pour un compte qui voit les pièces commerciales
 * @param {() => Promise<object[]>} [charge] lecture des ouvertures
 * @returns {{liste: object[], nonLues: number, nouvelle: ?object, oublieNouvelle: () => void,
 *   marqueLues: () => void}} les ouvertures et ce qu'on en fait
 */
export function useConsultations(actif, charge = consultationsApi.liste) {
  const [liste, setListe] = useState([]);
  const [nouvelle, setNouvelle] = useState(null);
  const connues = useRef(null);

  const relit = useCallback(() => {
    Promise.resolve()
      .then(charge)
      .then((recues) => {
        const lignes = Array.isArray(recues) ? recues : [];
        if (connues.current) {
          const arrivee = lignes.find((c) => !c.lu && !connues.current.has(c.id));
          if (arrivee) setNouvelle(arrivee);
        }
        connues.current = new Set(lignes.map((c) => c.id));
        setListe(lignes);
      })
      .catch(() => {});
    // La lecture ne change pas d'une relecture à l'autre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!actif) return undefined;
    relit();
    return surRafraichissement(relit);
  }, [actif, relit]);

  const marqueLues = useCallback(() => {
    setListe((p) => (p.some((c) => !c.lu) ? p.map((c) => ({ ...c, lu: true })) : p));
    consultationsApi.lues().catch(() => {});
  }, []);

  return {
    liste,
    nonLues: liste.filter((c) => !c.lu).length,
    nouvelle,
    oublieNouvelle: () => setNouvelle(null),
    marqueLues,
  };
}

/**
 * La phrase d'une ouverture : « Jean Rauber a ouvert l'offre 03 ».
 *
 * @param {object} c ouverture
 * @param {string} [motOffre] mot de la société pour l'offre (« offre », « devis »)
 * @returns {string} la phrase
 */
export function phraseConsultation(c, motOffre = 'offre') {
  const qui = (c.client || '').trim() || tr('Le client');
  // La pièce se traduit entière : « l'offre 518 » restait en français au
  // milieu de la phrase traduite (« … hat l'offre 518 geöffnet »).
  // Hors du français, le nom de la pièce vient du dictionnaire par sa forme
  // « devis » : c'est elle que chaque langue traduit (« Offerte », « quote »).
  const modele =
    c.type === 'FACTURE'
      ? 'la facture {0}'
      : motOffre === 'devis' || !String(LANG).startsWith('fr')
        ? 'le devis {0}'
        : "l'offre {0}";
  const piece = c.numero ? trf(modele, c.numero) : tr(modele.replace(' {0}', ''));
  // Transmise depuis la page du client, à une personne nommée.
  if (c.nature === 'TRANSMISSION') {
    const a = (c.transmisNom || '') + (c.transmisEmail ? ' (' + c.transmisEmail + ')' : '');
    return trf('{0} a transmis {1} à {2}', (c.personneNom || '').trim() || qui, piece, a);
  }
  // L'aperçu A4 du courriel d'envoi s'est affiché : le message a été vu.
  // Certaines messageries chargent les images d'avance : « probablement ».
  // Capture d'écran faite dans l'application SwissTech (iPhone, Android 14+).
  if (c.nature === 'CAPTURE')
    return trf('{0} a fait une capture d’écran de {1}', (c.personneNom || '').trim() || qui, piece);
  if (c.nature === 'VU') return trf('{0} a probablement vu {1} dans l’e-mail', qui, piece);
  // Le PDF ouvert par le bouton de l'e-mail d'envoi, à la place de la pièce jointe.
  const pdf = c.nature === 'PDF';
  // Une autre personne, toujours connue par son nom : elle l'a donné sur la
  // page, ou l'a reçu de la transmission. Un autre navigateur sans nom est le
  // client lui-même (Gmail puis Chrome), jamais « une autre personne ».
  if (c.autrePersonne && c.personneNom) {
    // Partie en un seul e-mail à plusieurs personnes : l'une d'elles, pas
    // forcément un lien transmis.
    if (c.plusieursDestinataires) {
      if (pdf) return trf('{0} ({2}) a ouvert {1} en PDF', c.personneNom, piece, qui);
      return c.nature === 'TELECHARGEMENT'
        ? trf('{0} ({2}) a téléchargé {1}', c.personneNom, piece, qui)
        : trf('{0} ({2}) a ouvert {1}', c.personneNom, piece, qui);
    }
    if (pdf)
      return trf('{0} a ouvert {1} en PDF (lien transmis par {2})', c.personneNom, piece, qui);
    return c.nature === 'TELECHARGEMENT'
      ? trf('{0} a téléchargé {1} (lien transmis par {2})', c.personneNom, piece, qui)
      : trf('{0} a ouvert {1} (lien transmis par {2})', c.personneNom, piece, qui);
  }
  if (pdf) return trf('{0} a ouvert {1} en PDF', qui, piece);
  if (c.nature === 'TELECHARGEMENT') return trf('{0} a téléchargé {1} en PDF', qui, piece);
  // Par le bouton « Voir en ligne » du PDF qu'il a reçu.
  if (c.depuisPdf) return trf('{0} a ouvert {1} depuis le PDF', qui, piece);
  return `${qui} a ouvert ${piece}`;
}
